import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import CategoryGrid from '../components/CategoryGrid';
import PlaceCard from '../components/PlaceCard';
import PageLoader from '../components/PageLoader';
import ContactFooter from '../components/ContactFooter';
import { useCategories } from '../hooks/useCategories';
import { useScrollRestoration } from '../hooks/useScrollRestoration';
import { getMostSavedPlaces, getCollections } from '../services/api';
import { MOSAIC_SKELETON } from '../utils/categoryMosaic';

// До конца слайдера из тридцати карточек никто не пролистывает
const MOST_SAVED_LIMIT = 12;

const CategoriesPage = () => {
  const navigate = useNavigate();
  const scrollRef = useScrollRestoration();
  const { categories, loading: categoriesLoading } = useCategories();
  const [mostSavedPlaces, setMostSavedPlaces] = useState([]);
  const [placesLoading, setPlacesLoading] = useState(true);
  const [collections, setCollections] = useState([]);
  const [collectionsLoading, setCollectionsLoading] = useState(true);

  // Загрузка мест, которые сохраняли чаще всего
  useEffect(() => {
    const loadMostSavedPlaces = async () => {
      try {
        setPlacesLoading(true);
        const response = await getMostSavedPlaces(MOST_SAVED_LIMIT);
        setMostSavedPlaces(response.data || []);
      } catch (error) {
        // Handle loading error silently
      } finally {
        setPlacesLoading(false);
      }
    };

    loadMostSavedPlaces();
  }, []);

  // Загрузка подборок
  useEffect(() => {
    const loadCollections = async () => {
      try {
        setCollectionsLoading(true);
        const response = await getCollections();
        setCollections(response.data || []);
      } catch (error) {
        // Handle loading error silently
      } finally {
        setCollectionsLoading(false);
      }
    };

    loadCollections();
  }, []);

  return (
    /* Экран поднимается по готовности категорий — своего главного блока.
       Раньше isLoading объединял три запроса, и пользователь не видел
       ничего, пока не догрузятся места и подборки; скелетоны внутри блоков
       при этом были написаны, но перекрыты PageLoader */
    <PageLoader loading={categoriesLoading}>
      {/* Все секции по одной линии — поля экрана px-4 (16px), брендбук 10.1.
          Вертикальный ритм между секциями один: mt-8 (32px) */}
      <div
        ref={scrollRef}
        className="min-h-screen bg-theme-bg pb-6 max-w-app mx-auto"
      >
        {/* Header */}
        <section className="px-4 pb-4" style={{ paddingTop: 'calc(var(--safe-area-top) + var(--tg-header-height) + 16px)' }}>
          <h1 className="text-h2 text-theme-text-primary">Категории</h1>
        </section>

        {/* Блок Категории */}
        <section className="px-4">
          {categoriesLoading ? (
            <div className="grid grid-cols-3 gap-2">
              {MOSAIC_SKELETON.map((style, i) => (
                <div
                  key={i}
                  className="bg-theme-card-secondary rounded-xl animate-pulse"
                  style={style}
                />
              ))}
            </div>
          ) : categories.length > 0 ? (
            <CategoryGrid categories={categories} />
          ) : (
            <div className="bg-theme-card-secondary rounded-md p-6 text-center">
              <p className="text-theme-text-secondary text-body">Пока нет категорий</p>
            </div>
          )}
        </section>

        {/* Блок "Подборки" */}
        <section id="collections" className="mt-8 px-4">
          <h2 className="mb-4 text-h2 text-theme-text-primary">
            Подборки
          </h2>

          {collectionsLoading ? (
            <div className="flex gap-3 overflow-x-auto scrollbar-hide">
              {[1, 2, 3, 4].map((i) => (
                <div
                  key={i}
                  className="flex-shrink-0 bg-theme-card-secondary rounded-md h-64 animate-pulse"
                  style={{ width: '180px' }}
                />
              ))}
            </div>
          ) : collections.length > 0 ? (
            /* Нативный скролл вместо Swiper: системная инерция реагирует на
               силу свайпа, JS-физика Swiper — нет. Как в HeroCarousel. */
            <div className="flex gap-3 overflow-x-auto scrollbar-hide -mx-1 px-1">
              {collections.map((collection, index) => (
                <div
                  key={collection.id}
                  onClick={() => navigate(`/collection/${collection.slug}`)}
                  className="flex-shrink-0 cursor-pointer transition-transform active:scale-95"
                  style={{ width: '180px' }}
                >
                  <div className="bg-theme-card border border-theme-border rounded-md overflow-hidden" style={{ height: '240px' }}>
                    <img
                      src={collection.image?.url || collection.image}
                      alt={collection.title}
                      className="w-full h-full object-cover"
                      loading={index < 2 ? 'eager' : 'lazy'}
                      decoding="async"
                    />
                  </div>
                  <h3 className="text-body font-semibold text-theme-text-primary mt-2 line-clamp-2">
                    {collection.title}
                  </h3>
                </div>
              ))}
            </div>
          ) : (
            <div className="bg-theme-card-secondary rounded-md p-6 text-center">
              <p className="text-theme-text-secondary text-body">
                Пока нет подборок
              </p>
            </div>
          )}
        </section>

        {/* Блок "Сохраняли чаще всего" */}
        <section className="mt-8 px-4">
          <h2 className="mb-4 text-h2 text-theme-text-primary">
            Сохраняли чаще всего
          </h2>

          {placesLoading ? (
            <div className="flex gap-3 overflow-x-auto scrollbar-hide">
              {[1, 2, 3, 4].map((i) => (
                <div
                  key={i}
                  className="flex-shrink-0 bg-theme-card-secondary rounded-md h-64 animate-pulse"
                  style={{ width: '180px' }}
                />
              ))}
            </div>
          ) : mostSavedPlaces.length > 0 ? (
            /* Нативный скролл вместо Swiper — см. комментарий в блоке подборок */
            <div className="flex gap-3 overflow-x-auto scrollbar-hide -mx-1 px-1">
              {mostSavedPlaces.map((place) => (
                <div key={place.id} className="flex-shrink-0" style={{ width: '180px' }}>
                  <PlaceCard place={place} source="categories" />
                </div>
              ))}
            </div>
          ) : (
            <div className="bg-theme-card-secondary rounded-md p-6 text-center">
              <p className="text-theme-text-secondary text-body">
                Пока нет популярных мест
              </p>
            </div>
          )}
        </section>

        <div className="px-4">
          <ContactFooter />
        </div>
      </div>
    </PageLoader>
  );
};

export default CategoriesPage;
